/**
 * Composable universel pour la structuration des données
 * Gère la création de hiérarchies, les agents, et les mappings automatiques
 */
export function useDataStructuring() {

  /**
   * Structure les données selon le format MEMoS
   */
  function structureDataForExport(flatData: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};

    // Helper pour set un chemin profond
    function setDeep(obj: Record<string, any>, path: string, value: any) {
      const parts = path.split('.');
      let current = obj;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
    }

    // Détecter automatiquement les sections d'agents
    const agentSections = detectAgentSections(flatData);
    
    // Traiter chaque clé
    const keysToProcess = Object.keys(flatData);
    for (const key of keysToProcess) {
      // Gestion spéciale pour les agents
      if (isAgentSection(key, agentSections)) {
        const sectionName = getSectionName(key);
        if (!result[sectionName]) {
          result[sectionName] = createAgentsStructure(flatData, sectionName);
        }
        continue; // Ne pas setDeep les champs individuels d'agents
      }
      
      // Gestion normale pour les autres champs
      // Si la valeur est un objet (structure hiérarchique), l'ajouter directement
      if (flatData[key] && typeof flatData[key] === 'object' && !Array.isArray(flatData[key])) {
        result[key] = flatData[key];
      } else {
        // Sinon, utiliser setDeep pour les chemins plats
        setDeep(result, key, flatData[key]);
      }
    }

    return result;
  }

  /**
   * Détecte automatiquement les sections d'agents dans les données
   */
  function detectAgentSections(flatData: Record<string, any>): string[] {
    const sections = new Set<string>();
    
    for (const key of Object.keys(flatData)) {
      const parts = key.split('.');
      if (parts.length >= 3 && parts[1] === 'Agent') {
        sections.add(parts[0]);
      }
    }
    
    return Array.from(sections);
  }

  /**
   * Vérifie si une clé appartient à une section d'agents
   */
  function isAgentSection(key: string, agentSections: string[]): boolean {
    const parts = key.split('.');
    return parts.length >= 3 && parts[1] === 'Agent' && agentSections.includes(parts[0]);
  }

  /**
   * Extrait le nom de la section depuis une clé d'agent
   */
  function getSectionName(key: string): string {
    return key.split('.')[0];
  }

  /**
   * Crée la structure des agents selon le format MEMoS
   */
  function createAgentsStructure(flatData: Record<string, any>, section: string): any[] {
    const agentFields: Record<string, any[]> = {};
    const agentSimpleFields: Record<string, any> = {};
    
    // Collecter tous les champs d'agents
    for (const [key, value] of Object.entries(flatData)) {
      if (key.startsWith(`${section}.Agent.`)) {
        const fieldName = key.replace(`${section}.Agent.`, '');
        
        if (Array.isArray(value)) {
          agentFields[fieldName] = value;
        } else {
          agentSimpleFields[fieldName] = value;
        }
      }
    }
    
    // Déterminer le nombre d'agents
    const maxLength = Object.values(agentFields).length > 0 
      ? Math.max(...Object.values(agentFields).map(arr => arr.length))
      : 1;
    
    // Déterminer l'ordre des champs
    const fieldOrder = getAgentFieldOrder(Object.keys(flatData), section);
    
    // Construire le tableau d'agents
    const agents = [];
    for (let i = 0; i < maxLength; i++) {
      const agent: Record<string, any> = {};
      
      for (const fieldName of fieldOrder) {
        const memosFieldName = mapToMemosFieldName(fieldName);
        
        if (agentFields[fieldName] && i < agentFields[fieldName].length) {
          agent[memosFieldName] = agentFields[fieldName][i];
        } else if (agentSimpleFields[fieldName]) {
          if (Array.isArray(agentSimpleFields[fieldName])) {
            agent[memosFieldName] = agentSimpleFields[fieldName][i] || agentSimpleFields[fieldName][0];
          } else {
            agent[memosFieldName] = agentSimpleFields[fieldName];
          }
        }
      }
      
      if (Object.keys(agent).length > 0) {
        agents.push(agent);
      }
    }
    
    return agents;
  }

  /**
   * Détermine l'ordre des champs d'agents selon le mapping
   */
  function getAgentFieldOrder(orderedKeys: string[], section: string): string[] {
    const agentKeys = orderedKeys
      .filter(key => key.startsWith(`${section}.Agent.`))
      .map(key => key.replace(`${section}.Agent.`, ''));
    
    return agentKeys;
  }

  /**
   * Mappe un nom de champ vers le format MEMoS
   */
  function mapToMemosFieldName(fieldName: string): string {
    const fieldMappings: Record<string, string> = {
      'uuid': 'UUID',
      'last_name': 'LastName',
      'first_name': 'FirstName',
      'Name': 'Name',
      'idref_id': 'AgentAurehalId',
      'affiliation': 'Affiliation',
      'affiliation_id': 'AffiliationAurehalId',
      'group': 'Group',
      'is_legal_entity': 'IsLegalEntity',
      'roleslist': 'Roles'
    };
    
    return fieldMappings[fieldName] || fieldName;
  }

  /**
   * Structure les données pour l'import (bidirectionnel)
   */
  function structureDataForImport(flatData: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};

    function setDeep(obj: Record<string, any>, path: string, value: any) {
      const parts = path.split('.');
      let current = obj;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
    }

    for (const [key, value] of Object.entries(flatData)) {
      setDeep(result, key, value);
    }

    return result;
  }

  /**
   * Aplatit une structure hiérarchique en données plates
   */
  function flattenData(data: Record<string, any>, prefix = ''): Record<string, any> {
    const result: Record<string, any> = {};

    for (const [key, value] of Object.entries(data)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(result, flattenData(value, fullKey));
      } else {
        result[fullKey] = value;
      }
    }

    return result;
  }

  /**
   * Nettoie les données en supprimant les champs vides
   */
  function removeEmptyFields(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(removeEmptyFields).filter(item => 
        item !== null && item !== undefined && item !== ''
      );
    }

    if (typeof obj === 'object') {
      const cleaned: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        if (value !== null && value !== undefined && value !== '') {
          const cleanedValue = removeEmptyFields(value);
          if (cleanedValue !== null && cleanedValue !== undefined && cleanedValue !== '') {
            cleaned[key] = cleanedValue;
          }
        }
      }
      return cleaned;
    }

    return obj;
  }

  return {
    structureDataForExport,
    structureDataForImport,
    flattenData,
    removeEmptyFields,
    detectAgentSections,
    createAgentsStructure
  };
} 